import api from "@/config/api";
import { handleApiError } from "@/utils/ErrorHandler";
import { formatDate } from "../../utils/FormatDate";
import { createPaginationModule, defaultPagination } from "@/store/common/pagination";

const signupPagination = createPaginationModule('SIGNUP');

const state = {
    openPositions: [],
    userSkills: [],
    userInterestSkills: [],
    availableSkills: [],
    skillOptions: [],
    activityOptions: [],
    totalOpenings: 0,
    loading: false,
    error: null,
    pagination: defaultPagination(), // ✅ shared pagination state
    lastSearch: "", // ✅ to persist search term between pages
};

const mutations = {
    ...signupPagination.mutations, // ✅ module-specific pagination mutations

    SET_OPEN_POSITIONS(state, positions) {
        state.openPositions = positions;
    },
    SET_USER_SKILLS(state, skills) {
        state.userSkills = skills;
    },
    SET_USER_INTEREST_SKILLS(state, skills) {
        state.userInterestSkills = skills;
    },
    SET_AVAILABLE_SKILLS(state, skills) {
        state.availableSkills = skills;
    },
    SET_SKILL_OPTIONS(state, skills) {
        state.skillOptions = skills;
    },
    SET_ACTIVITY_OPTIONS(state, activities) {
        state.activityOptions = activities;
    },
    SET_TOTAL_OPENINGS(state, total) {
        state.totalOpenings = total;
    },
    SET_LOADING(state, val) {
        state.loading = val;
    },
    SET_ERROR(state, err) {
        state.error = err;
    },
    SET_USER_INTERESTS(state, interests) {
        state.userInterestSkills = interests;
    },
    ADD_TO_INTERESTS(state, skills) {
        state.userInterestSkills.push(...skills);
        state.availableSkills = state.availableSkills.filter(
            (s) => !skills.some((sel) => sel.skillid === s.skillid)
        );
    },
    REMOVE_FROM_INTERESTS(state, skill) {
        state.userInterestSkills = state.userInterestSkills.filter(
            (i) => i.skillid !== skill.skillid
        );
        state.availableSkills.push(skill);
    },
    MOVE_ALL_TO_INTERESTS(state) {
        state.userInterestSkills.push(...state.availableSkills);
        state.availableSkills = [];
    },
    MOVE_ALL_TO_AVAILABLE(state) {
        state.availableSkills.push(...state.userInterestSkills);
        state.userInterestSkills = [];
    },
};

const actions = {
    /**
     * ✅ Fetch open positions with pagination + optional search
     * API: /open-positions?locationid=&personid=&search=&page_number=&page_size=
     */
    async getOpenPositions(
        { commit },
        { locationId, personId, search = "", skillId = "", skillIds = [], activityIds = [], futureDays = 60, page = 1, pageSize = 10 }
    ) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);

        try {
            const response = await api.get(`/open-positions`, {
                params: {
                    locationid: locationId,
                    personid: personId,
                    search,
                    skillid: skillId || undefined,
                    skillids: skillIds.length ? skillIds : undefined,
                    activityids: activityIds.length ? activityIds : undefined,
                    future_days: futureDays,
                    page_number: page,
                    page_size: pageSize,
                },
            });

            const data = response.data.output.data;

            // ✅ Map open positions
            const mappedPositions = (data.open_positions || []).map((item) => ({
                ...item,
                date: formatDate(item.date),
            }));

            commit("SET_OPEN_POSITIONS", mappedPositions);
            commit("SET_USER_SKILLS", response.data.output.user_skills || data.user_skills || []);
            commit("SET_USER_INTEREST_SKILLS", response.data.output.user_interest_skills || []);
            commit("SET_AVAILABLE_SKILLS", response.data.output.available_skills || []);
            commit("SET_SKILL_OPTIONS", data.skill_options || []);
            commit("SET_ACTIVITY_OPTIONS", data.activity_options || []);
            commit("SET_TOTAL_OPENINGS", data.total_openings || 0);

            // ✅ Update shared pagination info
            if (data.pagination) {
                commit(signupPagination.SET_PAGINATION, {
                    currentPage: data.pagination.current_page,
                    totalPages: data.pagination.total_pages,
                    totalRecords: data.pagination.total_records,
                    pageSize: data.pagination.page_size,
                    hasPrevious: data.pagination.has_previous,
                    hasNext: data.pagination.has_next,
                });
            }

            return mappedPositions;
        } catch (error) {
            const message = handleApiError(error, "Failed to fetch open positions");
            commit("SET_ERROR", message);
            throw new Error(message);
        } finally {
            commit("SET_LOADING", false);
        }
    },

    /**
     * ✅ Handle page change while preserving search term
     */
    async changeOpenPositionsPage({ state, dispatch }, { locationId, personId, page, pageSize, futureDays = 60 }) {
        const currentSearch = state.lastSearch || "";
        await dispatch("getOpenPositions", {
            locationId,
            personId,
            search: currentSearch,
            page,
            pageSize,
            futureDays,
        });
    },

    /**
     * ✅ Update search term and reset pagination
     */
    async updateOpenPositionsSearch({ dispatch, state }, { locationId, personId, search, futureDays = 60 }) {
        state.lastSearch = search;
        await dispatch("getOpenPositions", {
            locationId,
            personId,
            search,
            page: 1,
            pageSize: state.pagination.pageSize,
            futureDays,
        });
    },

    /**
     * ✅ Save interest skills
     */
    async saveInterestSkills({ commit }, { locationId, personId, skills }) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);

        try {
            const response = await api.post(`/open-positions/save-interest-skills`, {
                locationid: locationId,
                personid: personId,
                skills,
            });
            return response.data;
        } catch (error) {
            const message = handleApiError(error, "Failed to save interest skills");
            commit("SET_ERROR", message);
            throw new Error(message);
        } finally {
            commit("SET_LOADING", false);
        }
    },

    /**
     * ✅ Sign up a person to a workgroup
    * API: POST /open-positions/signup
     */
    async signupForWorkgroup({ commit }, { personId, locationId, aiwg }) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);

        try {
            const response = await api.post("/open-positions/signup", {
                personid: personId,
                locationid: locationId,
                aiwg,
            });

            // ✅ If API returns success message, return it
            return response.data;
        } catch (error) {
            const message = handleApiError(error, "Failed to sign up for workgroup");
            commit("SET_ERROR", message);
            throw new Error(message);
        } finally {
            commit("SET_LOADING", false);
        }
    },
};

const getters = {
    allOpenPositions: (state) => state.openPositions,
    userSkills: (state) => state.userSkills,
    userInterestSkills: (state) => state.userInterestSkills,
    availableSkills: (state) => state.availableSkills,
    signupSkillOptions: (state) => state.skillOptions,
    signupActivityOptions: (state) => state.activityOptions,
    totalOpenings: (state) => state.totalOpenings,
    openPositionsLoading: (state) => state.loading,
    openPositionsError: (state) => state.error,
    signupFormPagination: (state) => state.pagination,
};

export default {
    state,
    mutations,
    actions,
    getters,
};